import React, { useCallback } from 'react';
import { useForm } from 'react-final-form';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { MarketingCheckboxes } from '@interlayer/js/app/components/main/MarketingPreferences/components/MarketingCheckboxes';

import { Checkbox } from 'js/components/common/Fields/Checkbox';
import I18n from 'js/components/common/I18n';

import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { clsb } from '@core/js/utils/classNames.util';

import {
    CONTACT_TYPES_FRONT,
    DEFAULT_MARKETING_PRODUCTS,
    MAIN_CONTACT_TYPES_FRONT,
    SELECT_ALL_FIELD
} from '@interlayer/js/app/components/main/MarketingPreferences/constants';
import { getContactPreferencesForSending } from '@interlayer/js/app/components/main/MarketingPreferences/helpers';
import { changeUserContactPreferences } from '@interlayer/js/app/components/popups/EmailChangePopup/components/server';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { useTranslations } from 'js/hooks/i18n';
import { useFetchUserDetails } from 'js/hooks/user';

import style from '../MarketingPreferences.scss';

export const useSaveMarketingPreferences = (products) => {
    const t = useTranslations('playtech.mobile.system.marketing-preferences.');
    const showNotification = useShowNotification();
    const fetchUserDetails = useFetchUserDetails();

    return useCallback((valuesToSend, shouldShowMessage = true) => {
        return changeUserContactPreferences(getContactPreferencesForSending(valuesToSend, products))
            .then(fetchUserDetails)
            .then(() => {
                if (shouldShowMessage) {
                    showNotification(t('message.successUpdate'));
                }
            })
            .catch(error => error.response?.data.errorMessage);
    }, [fetchUserDetails, products, showNotification, t]);
};

export const MarketingPreferencesFields = ({
    products = DEFAULT_MARKETING_PRODUCTS,
    contactTypes = [],
    onChange = null,
    tSuffix = '',
    isAdditionalContact = false,
    isAdditionalTextBlocks = false,
    isSlotsClubPlayerTags = false,
    isProductTitleHidden = false,
    isInfoBlockHidden = false,
    classNames = {}
}) => {
    const form = useForm();
    const t = useTranslations(`playtech.mobile.system.marketing-preferences.${tSuffix}`);
    const buildCls = clsb([style, classNames]);

    const handlerSelectAll = (e) => {
        const radioValue = e.target.checked;

        MAIN_CONTACT_TYPES_FRONT.forEach(checkboxName => {
            products.forEach(product => {
                form.change(`${product}_${checkboxName}`, radioValue);
            });
        });
    };

    return (
        <>
            {!contactTypes?.length && !isProductTitleHidden && (<div className={style.title}>{t('marketing.title')}</div>)}

            {isAdditionalTextBlocks && (
                <div className={style.additionalTextBlock}>
                    <I18n t={t} tKey="marketing.additionalTextBlock.before" />
                </div>
            )}

            <div className={buildCls(['checkboxWrap'])}>
                {contactTypes.length === 0 && (
                    <div className={style.selectAllContainer}>
                        <div className={style.toggleIconContainer}>
                            <SvgIcon
                                type="select-all"
                                className={clsx(style.toggleIcon, style.selectAllIcon)}
                            />

                            <span className={style.passiveLabel}>
                                {t('selectAll')}
                            </span>
                        </div>

                        <Checkbox
                            reverse
                            kind="toggle"
                            name={SELECT_ALL_FIELD}
                            handlerOnChange={handlerSelectAll}
                            classNames={{
                                checkbox: buildCls(['checkbox']),
                                label: buildCls(['checkboxLabel']),
                                control: buildCls(['checkboxControl'])
                            }}
                        />
                    </div>
                )}

                <MarketingCheckboxes
                    products={products}
                    onChange={onChange}
                    contactTypes={contactTypes}
                    isAdditionalContact={isAdditionalContact}
                    isProductTitleHidden={isProductTitleHidden}
                    classNames={{
                        checkboxLabel: style.label,
                        checkboxWrap: style.checkboxContainer,
                        checkboxControl: buildCls(['checkboxControl'])
                    }}
                />

                {isSlotsClubPlayerTags && (
                    <div className={style.selectAllContainer}>
                        <div className={style.toggleIconContainer}>
                            <SvgIcon
                                type="slots"
                                className={clsx(style.toggleIcon, style.selectAllIcon)}
                            />

                            <span className={style.passiveLabel}>
                                {t('optinSlotsClub')}
                            </span>
                        </div>

                        <Checkbox
                            reverse
                            kind="toggle"
                            name={CONTACT_TYPES_FRONT.slotClubParticipation}
                            classNames={{
                                checkbox: buildCls(['checkbox']),
                                label: buildCls(['checkboxLabel']),
                                control: buildCls(['checkboxControl'])
                            }}
                        />
                    </div>
                )}
            </div>

            {!isInfoBlockHidden && (
                <div className={buildCls(['infoPanel'])}>
                    <I18n className={buildCls(['buzzLimited'])} t={t} tKey="buzzLimited" />
                </div>
            )}

            {isAdditionalTextBlocks && (
                <div className={style.additionalTextBlock}>
                    <I18n t={t} tKey="marketing.additionalTextBlock.after" />
                </div>
            )}
        </>
    );
};
